/**
 * Camada de fumaça no mapa: o contorno das áreas que a NOAA marcou como
 * fumaça, trocando a cada quadro do loop.
 *
 * - Um quadro é um GeoJSON pequeno (~5–20 KB comprimido; ~200–2000 polígonos),
 *   então a camada vetorial comum do OpenLayers dá conta, e o estilo entra na
 *   composição da exportação.
 * - Os quadros de TODO o loop são baixados assim que a camada liga (4 por
 *   vez, do quadro atual para trás), e ficam na memória já projetados — o
 *   loop toca sem pedir nada. Os que saem da janela são descartados.
 * - A reprodução espera a fumaça do próximo quadro (`registerFrameGate`) e a
 *   exportação, a do atual; o que ainda não chegou deixa o anterior na tela
 *   (nunca outro horário com o rótulo deste: o painel diz "baixando").
 */
import type { ReactNode } from 'react';
import type Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import type OlMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { isAbortError } from '../lib/fetchRetry';
import { registerFrameGate } from '../loop/preloader';
import { registerFrameReadiness } from '../loop/render';
import { loopLoadAllowed, useLoopStore } from '../loop/store';
import { subscribeSelector } from '../lib/store';
import { GOES_PROJ, registerGoesProjection } from '../map/projection';
import { Z } from '../map/zIndex';
import { shownTimeOf, useTimeStore } from '../state/time';
import { carregarQuadro, FUMACA_ATRIBUICAO, type QuadroFumaca } from './api';
import { useSmokeStore, type FumacaNoQuadro } from './store';
import { quadroParaInstante } from './tempo';

/** O que o popup recebe ao clicar numa área. */
export interface AreaFumaca {
  km2: number;
  /** Quadro da NOAA (epoch ms). */
  t: number;
}

/** Cor da fumaça: bege quente, distinta das linhas brancas do mapa e do laranja dos focos. */
export const COR_FUMACA = '#f3dfbf';

const ESTILO = [
  // Casco escuro: o contorno claro se lê sobre nuvem branca e sobre a mata.
  new Style({ stroke: new Stroke({ color: 'rgba(24, 18, 12, 0.6)', width: 3.2 }) }),
  new Style({
    fill: new Fill({ color: 'rgba(243, 223, 191, 0.22)' }),
    stroke: new Stroke({ color: COR_FUMACA, width: 1.4 }),
  }),
];

const PARALELOS = 4;

type Entrada =
  | { estado: 'carregando'; ctrl: AbortController }
  | { estado: 'ok'; feicoes: Feature[] }
  | { estado: 'falhou' };

const formato = new GeoJSON();

/** GeoJSON (lon/lat) → features na projeção do mapa, com o horário e a área de cada uma. */
export function lerQuadro(cru: unknown, t: number): Feature[] {
  registerGoesProjection();
  const feicoes = formato.readFeatures(cru, { dataProjection: 'EPSG:4326', featureProjection: GOES_PROJ }) as Feature[];
  for (const f of feicoes) {
    const km2 = Number(f.get('km2'));
    f.set('fumaca', { km2: Number.isFinite(km2) ? km2 : 0, t } satisfies AreaFumaca, true);
  }
  return feicoes;
}

export class SmokeLayerManager {
  readonly layer: VectorLayer<VectorSource>;
  private readonly source: VectorSource;
  private readonly cache = new Map<string, Entrada>();
  private fila: QuadroFumaca[] = [];
  private emVoo = 0;
  private naTela: string | null = null;
  private tempos: number[] = [];
  private quadros: QuadroFumaca[] = [];
  private esperas = new Map<string, (() => void)[]>();
  private readonly paradas: (() => void)[] = [];

  constructor(
    private readonly map: OlMap,
    renderPopup: (a: AreaFumaca) => ReactNode | null,
  ) {
    this.source = new VectorSource({ attributions: FUMACA_ATRIBUICAO });
    this.layer = new VectorLayer({
      source: this.source,
      style: ESTILO,
      zIndex: Z.SMOKE,
      className: 'ol-layer amaview-smoke',
      properties: { id: 'smoke', title: 'Fumaça (NOAA)' },
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
    this.layer.set('popup', (feature: FeatureLike): ReactNode | null => {
      const a = feature.get('fumaca') as AreaFumaca | undefined;
      return a ? renderPopup(a) : null;
    });
    map.addLayer(this.layer);

    this.paradas.push(
      useSmokeStore.subscribe((a, b) => {
        if (a.opacidade !== b.opacidade) this.layer.setOpacity(a.opacidade);
        if (a.visivel !== b.visivel || a.indice !== b.indice) this.replanejar();
      }),
      useTimeStore.subscribe((a, b) => {
        // Antes do primeiro play cada quadro novo na tela pede a sua fumaça (sob demanda).
        if (a.frames !== b.frames || (a.index !== b.index && !loopLoadAllowed())) this.replanejar();
        else if (shownTimeOf(a) !== shownTimeOf(b)) this.mostrar();
      }),
      subscribeSelector(useLoopStore, (s) => loopLoadAllowed(s), () => this.replanejar()),
      registerFrameGate((i) => this.pronto(useTimeStore.getState().frames[i] ?? null)),
      registerFrameReadiness(() => this.quandoProntoAtual()),
    );
    this.layer.setOpacity(useSmokeStore.getState().opacidade);
    this.replanejar();
  }

  private quadroDe(t: number | null): QuadroFumaca | null {
    return quadroParaInstante(this.quadros, this.tempos, t);
  }

  /** O quadro da fumaça para o instante `t` já pode ser desenhado (ou não há o que desenhar)? */
  pronto(t: number | null): boolean {
    if (!useSmokeStore.getState().visivel) return true;
    const q = this.quadroDe(t);
    if (!q || q.n === 0) return true;
    const e = this.cache.get(q.c);
    return e !== undefined && e.estado !== 'carregando';
  }

  private quandoProntoAtual(): Promise<void> | void {
    const t = shownTimeOf(useTimeStore.getState());
    if (this.pronto(t)) return;
    const q = this.quadroDe(t)!;
    return new Promise((resolve) => {
      const lista = this.esperas.get(q.c) ?? [];
      lista.push(resolve);
      this.esperas.set(q.c, lista);
    });
  }

  /** Quadros necessários para o loop atual; baixa o que falta e descarta o que saiu. */
  private replanejar(): void {
    const { visivel, indice } = useSmokeStore.getState();
    this.layer.setVisible(visivel);
    this.quadros = indice?.quadros ?? [];
    this.tempos = this.quadros.map((q) => q.t);
    if (!visivel) {
      this.fila = [];
      this.mostrar();
      return;
    }
    const { frames, index } = useTimeStore.getState();
    const precisa = new Map<string, QuadroFumaca>();
    // Do quadro atual para trás, e depois o resto: o que está na tela chega primeiro.
    // Antes do primeiro play (portal parado na medição mais recente) só o
    // quadro na tela e os já baixados — como o satélite (loop/preloader.ts).
    const todoLoop = loopLoadAllowed();
    const ordem = frames.map((_, k) => (index - k + frames.length) % frames.length);
    for (const i of ordem) {
      const q = this.quadroDe(frames[i]);
      if (!q || q.n === 0) continue;
      if (todoLoop || i === index || this.cache.get(q.c)?.estado === 'ok') precisa.set(q.c, q);
    }
    for (const [c, e] of this.cache) {
      if (precisa.has(c)) continue;
      if (e.estado === 'carregando') e.ctrl.abort();
      this.cache.delete(c);
    }
    this.fila = [...precisa.values()].filter((q) => !this.cache.has(q.c));
    this.atualizarProgresso(precisa.size);
    this.bombear();
    this.mostrar();
  }

  private atualizarProgresso(total?: number): void {
    let baixados = 0;
    let falhas = 0;
    for (const e of this.cache.values()) {
      if (e.estado === 'ok') baixados++;
      else if (e.estado === 'falhou') falhas++;
    }
    const antes = useSmokeStore.getState().progresso;
    const t = total ?? antes.total;
    if (antes.baixados !== baixados || antes.falhas !== falhas || antes.total !== t) {
      useSmokeStore.setState({ progresso: { baixados, total: t, falhas } });
    }
  }

  private bombear(): void {
    while (this.emVoo < PARALELOS && this.fila.length) {
      const q = this.fila.shift()!;
      if (this.cache.has(q.c)) continue;
      const ctrl = new AbortController();
      this.cache.set(q.c, { estado: 'carregando', ctrl });
      this.emVoo++;
      carregarQuadro(q.c, ctrl.signal)
        .then((cru) => {
          if (this.cache.get(q.c)?.estado !== 'carregando') return;
          this.cache.set(q.c, { estado: 'ok', feicoes: lerQuadro(cru, q.t) });
        })
        .catch((e: unknown) => {
          if (isAbortError(e) || this.cache.get(q.c)?.estado !== 'carregando') return;
          console.warn(`[AmaView] Fumaça ${q.c} não carregou:`, e);
          this.cache.set(q.c, { estado: 'falhou' });
        })
        .finally(() => {
          this.emVoo--;
          this.atualizarProgresso();
          this.mostrar();
          // Depois de desenhar: a exportação espera o quadro NA TELA.
          const esperas = this.esperas.get(q.c);
          if (esperas && this.cache.get(q.c)?.estado !== 'carregando') {
            this.esperas.delete(q.c);
            esperas.forEach((r) => r());
          }
          this.bombear();
        });
    }
  }

  /** Recoloca na fila os quadros que falharam (ex.: a conexão caiu por um minuto). */
  tentarDeNovo(): void {
    let algum = false;
    for (const [c, e] of this.cache) {
      if (e.estado === 'falhou') {
        this.cache.delete(c);
        algum = true;
      }
    }
    if (algum) this.replanejar();
  }

  /** Desenha o quadro do instante exibido. */
  private mostrar(): void {
    const { visivel } = useSmokeStore.getState();
    const t = shownTimeOf(useTimeStore.getState());
    const q = visivel ? this.quadroDe(t) : null;
    let situacao: FumacaNoQuadro;
    if (!q) {
      this.trocar(null, []);
      situacao = { estado: 'sem', t: null };
    } else if (q.n === 0) {
      this.trocar(q.c, []);
      situacao = { estado: 'ok', t: q.t };
    } else {
      const e = this.cache.get(q.c);
      if (e?.estado === 'ok') {
        this.trocar(q.c, e.feicoes);
        situacao = { estado: 'ok', t: q.t };
      } else if (e?.estado === 'falhou') {
        this.trocar(null, []);
        situacao = { estado: 'falhou', t: q.t };
      } else {
        // Ainda baixando: o anterior continua na tela até este chegar.
        situacao = { estado: 'carregando', t: q.t };
        if (!e) {
          this.fila.unshift(q);
          this.bombear();
        }
      }
    }
    const antes = useSmokeStore.getState().noQuadro;
    if (antes.estado !== situacao.estado || antes.t !== situacao.t) useSmokeStore.setState({ noQuadro: situacao });
  }

  private trocar(c: string | null, feicoes: Feature[]): void {
    if (c === this.naTela) return;
    this.naTela = c;
    this.source.clear(true);
    if (feicoes.length) this.source.addFeatures(feicoes);
  }

  dispose(): void {
    this.paradas.forEach((p) => p());
    for (const e of this.cache.values()) if (e.estado === 'carregando') e.ctrl.abort();
    this.cache.clear();
    this.esperas.forEach((l) => l.forEach((r) => r()));
    this.esperas.clear();
    this.source.clear(true);
    this.map.removeLayer(this.layer);
  }
}
